import React, { useState } from 'react';
import { CheckIcon, ChevronDownIcon, MinusIcon } from 'lucide-react';
import { SmartLink } from '../SmartLink';
import { formatRwf } from '../../utils/currency';
import type { Billing, ComparisonCell, ComparisonGroup, Plan, PlanId } from '../../types/pricing';
type ComparisonTableProps = {
  plans: Plan[];
  groups: ComparisonGroup[];
  billing: Billing;
};
export function ComparisonTable({
  plans,
  groups,
  billing
}: ComparisonTableProps) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [mobilePlan, setMobilePlan] = useState<PlanId>('growth');
  const toggle = (title: string) => setCollapsed(c => ({
    ...c,
    [title]: !c[title]
  }));
  const priceLabel = (plan: Plan) => billing === 'annual' ? `${formatRwf(plan.annualTotal)}/year` : `${formatRwf(plan.monthly)}/month`;
  const selected = plans.find(p => p.id === mobilePlan) ?? plans[0];
  return <section className="w-full bg-white py-20" aria-labelledby="compare-heading" data-id="element-927" data-mp="jsnnmkehq9c5">
      <div className="mx-auto max-w-shell px-6" data-id="element-928" data-mp="1b8u0hxi2h1vv">
        <h2 id="compare-heading" className="display text-center text-[30px] font-semibold text-ink sm:text-[40px]" data-id="element-929" data-mp="1b8244kgnb04j">
          Compare every feature
        </h2>
        <p className="mx-auto mt-4 max-w-[560px] text-center text-[16px] leading-[1.5] text-ink-muted" data-id="element-930" data-mp="1l6842wiw5w6r">
          See exactly what’s included in each plan.
        </p>

        {/* Desktop table */}
        <table className="mt-12 hidden w-full border-separate border-spacing-0 lg:table" data-id="element-931" data-mp="sim1nrlegxlh">
          <caption className="sr-only" data-id="element-932" data-mp="xgnmagyh54z2">Feature comparison across Kayko plans</caption>
          <colgroup data-id="element-933" data-mp="12g9wsimx4746">
            <col className="w-[32%]" data-id="element-934" data-mp="12g07yydm0602" />
            {plans.map(p => <col key={p.id} className="w-[17%]" data-id="element-935" data-mp="17705o5ujys3m" />)}
          </colgroup>
          <thead data-id="element-936" data-mp="178tdvpktdkdq">
            <tr data-id="element-937" data-mp="9rum28atkayn">
              <th scope="col" className="sticky top-[64px] z-10 border-b border-canvas-line bg-white py-5 text-left align-bottom" data-id="element-938" data-mp="9se5jtg4b7es">
                <span className="sr-only" data-id="element-939" data-mp="ep4meizqy719">Feature</span>
              </th>
              {plans.map(plan => <th key={plan.id} scope="col" className={`sticky top-[64px] z-10 border-b border-canvas-line px-4 py-5 text-left align-bottom font-normal ${plan.popular ? 'rounded-t-[12px] bg-brand-soft' : 'bg-white'}`} data-id="element-940" data-mp="6o6lg6g6ppow">
                  <span className="block text-[16px] font-semibold text-ink" data-id="element-941" data-mp="3jw9qbwvzft64">{plan.name}</span>
                  <span className="mt-0.5 block whitespace-nowrap text-[12.5px] tabular-nums text-ink-muted" data-id="element-942" data-mp="3exgf6b464e94">
                    {formatRwf(billing === 'annual' ? plan.annualTotal : plan.monthly)}
                    <span className="block" data-id="element-943" data-mp="3ovmfwpe8nece">{billing === 'annual' ? 'per year' : 'per month'}</span>
                  </span>
                  <SmartLink href={plan.cta.href} className={`mt-3 inline-block rounded-[7px] px-3.5 py-1.5 text-[13px] font-semibold transition-colors duration-150 ease-out ${plan.popular ? 'bg-brand text-white hover:bg-brand-hover' : 'border border-ink/20 bg-white text-ink hover:border-ink/45'}`} data-id="element-944" data-mp="193p3i0bt2x25">
                    {plan.cta.label}
                  </SmartLink>
                </th>)}
            </tr>
          </thead>
          {groups.map(group => {
          const isCollapsed = collapsed[group.title];
          return <tbody key={group.title} data-id="element-945" data-mp="1zequ6qq0qsj3">
                <tr data-id="element-946" data-mp="1zdpkoqonie3h">
                  <th colSpan={plans.length + 1} scope="colgroup" className="p-0 text-left" data-id="element-947" data-mp="1ln2iupnlg836">
                    <GroupButton title={group.title} collapsed={!!isCollapsed} onClick={() => toggle(group.title)} data-id="element-948" data-mp="1ll9j7ubeeo4o" />
                  </th>
                </tr>
                {!isCollapsed && group.rows.map(row => <tr key={row.label} data-id="element-949" data-mp="2i58m4t311k3e">
                      <th scope="row" className="border-b border-canvas-line py-3.5 pr-4 text-left text-[14px] font-normal text-ink-soft" data-id="element-950" data-mp="2i73541uqn24u">
                        {row.label}
                      </th>
                      {plans.map(plan => <td key={plan.id} className={`border-b border-canvas-line px-4 py-3.5 ${plan.popular ? 'bg-brand-soft/60' : ''}`} data-id="element-951" data-mp="khpjkza5nshv">
                          <Cell value={row.values[plan.id]} data-id="element-952" data-mp="1d63l3p0gd6y6" />
                        </td>)}
                    </tr>)}
              </tbody>;
        })}
        </table>

        {/* Mobile: one plan at a time */}
        <div className="mt-10 lg:hidden" data-id="element-953" data-mp="zg02d80s7vdh">
          <div className="sticky top-[64px] z-10 -mx-6 border-b border-canvas-line bg-white px-6 pb-4 pt-3" data-id="element-954" data-mp="zeys35rlbcwt">
            <div role="tablist" aria-label="Choose a plan to compare" className="grid grid-cols-4 gap-1 rounded-[10px] bg-canvas-warm p-1" data-id="element-955" data-mp="1vz7d75y0vbqo">
              {plans.map(plan => {
              const active = plan.id === mobilePlan;
              return <button key={plan.id} type="button" role="tab" aria-selected={active} aria-controls="compare-mobile-panel" onClick={() => setMobilePlan(plan.id)} className={`rounded-[7px] px-1 py-2 text-[12.5px] font-semibold transition-colors duration-150 ease-out ${active ? 'bg-white text-ink shadow-[0_1px_3px_rgba(11,11,11,0.12)]' : 'text-ink-muted'}`} data-id="element-956" data-mp="3es8xvvt7qg4c">
                    {plan.name}
                  </button>;
            })}
            </div>
            <div className="mt-3 flex items-center justify-between gap-3" data-id="element-957" data-mp="fdnehvpnvzfs">
              <p className="text-[13px] text-ink-muted" data-id="element-958" data-mp="ffglxd83mnou">{priceLabel(selected)}</p>
              <SmartLink href={selected.cta.href} className={`shrink-0 rounded-[7px] px-3.5 py-1.5 text-[13px] font-semibold ${selected.popular ? 'bg-brand text-white' : 'border border-ink/20 bg-white text-ink'}`} data-id="element-959" data-mp="kd8du78qu9t1">
                {selected.cta.label}
              </SmartLink>
            </div>
          </div>

          <div id="compare-mobile-panel" role="tabpanel" aria-label={`${selected.name} features`} data-id="element-960" data-mp="2le69wbhidc18">
            {groups.map(group => {
            const isCollapsed = collapsed[group.title];
            return <div key={group.title} data-id="element-961" data-mp="2v4hr2zq7dhq2">
                  <GroupButton title={group.title} collapsed={!!isCollapsed} onClick={() => toggle(group.title)} data-id="element-962" data-mp="394afgoe9bluv" />
                  {!isCollapsed && <dl data-id="element-963" data-mp="3468s3r9st0dl">
                      {group.rows.map(row => <div key={row.label} className="flex items-center justify-between gap-4 border-b border-canvas-line py-3.5" data-id="element-964" data-mp="1erapc1c9399c">
                          <dt className="text-[14px] text-ink-soft" data-id="element-965" data-mp="19tj23kmjwbbh">{row.label}</dt>
                          <dd className="shrink-0 text-right" data-id="element-966" data-mp="3lfjrihu7wrk7">
                            <Cell value={row.values[selected.id]} data-id="element-967" data-mp="3qe4idqnkxyvz" />
                          </dd>
                        </div>)}
                    </dl>}
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}
function GroupButton({
  title,
  collapsed,
  onClick
}: {
  title: string;
  collapsed: boolean;
  onClick: () => void;
}) {
  return <button type="button" aria-expanded={!collapsed} onClick={onClick} className="flex w-full items-center justify-between border-b border-canvas-line pb-3 pt-8 text-left" data-id="element-968" data-mp="37oy54j5k1zhk">
      <span className="text-[15px] font-semibold text-ink" data-id="element-969" data-mp="2xppd3vu92uxu">{title}</span>
      <ChevronDownIcon className={`h-4 w-4 text-ink-muted transition-transform duration-200 ease-out ${collapsed ? '-rotate-90' : ''}`} aria-hidden="true" data-id="element-970" data-mp="qzspdf8zjrdn" />
    </button>;
}
function Cell({
  value
}: {
  value: ComparisonCell;
}) {
  if (value === true) {
    return <>
        <CheckIcon className="h-[18px] w-[18px] text-brand" strokeWidth={2.5} aria-hidden="true" data-id="element-971" data-mp="3avth2yvugbn0" />
        <span className="sr-only" data-id="element-972" data-mp="3630dwfg0xbd1">Included</span>
      </>;
  }
  if (value === false) {
    return <>
        <MinusIcon className="ml-auto h-4 w-4 text-ink-muted/60 lg:ml-0" aria-hidden="true" data-id="element-973" data-mp="314fm94eu8007" />
        <span className="sr-only" data-id="element-974" data-mp="2w6nmjo7yb9lg">Not included</span>
      </>;
  }
  return <span className="text-[14px] font-medium text-ink" data-id="element-975" data-mp="2s40y7xaho6t0">{value}</span>;
}